import React, { useMemo } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { ColumnDef } from '@tanstack/react-table';
import { handleRenderCountriesList } from 'views/Localization/utils';

import ActionCell from 'components/DataTableComponent/components/ActionCell';
import Switch from 'components/Switch/Switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui';

import { ILanguageTableRow } from '../AddedLanguagesTable.types';
import { formatCountryTooltip } from '../AddedLanguagesTable.utils';

interface UseLanguageTableColumnsProps {
  selectedLanguage: ILanguageTableRow | null;
  onToggle: (row: ILanguageTableRow) => void;
  onEdit: (row: ILanguageTableRow) => void;
  onDelete: (row: ILanguageTableRow) => void;
}

interface UseLanguageTableColumnsReturn {
  columns: ColumnDef<ILanguageTableRow>[];
}

/**
 * Hook that builds the column definitions for the added languages table.
 *
 * @example
 * const { columns } = useLanguageTableColumns({
 *   selectedLanguage,
 *   onToggle: handleToggleLanguage,
 *   onEdit: handleEdit,
 *   onDelete: handleDelete
 * });
 */
export const useLanguageTableColumns = ({
  selectedLanguage,
  onToggle,
  onEdit,
  onDelete
}: UseLanguageTableColumnsProps): UseLanguageTableColumnsReturn => {
  const columns = useMemo((): ColumnDef<ILanguageTableRow>[] => [
    {
      accessorKey: 'language',
      header: 'Display Name',
      enableSorting: true,
      cell: ({ getValue }) => getValue(),
      size: 200
    },
    {
      accessorKey: 'isoCode',
      header: 'Language Code',
      enableSorting: true,
      cell: ({ getValue }) => getValue(),
      size: 200
    },
    {
      accessorKey: 'country',
      header: 'Country',
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const countryA = rowA.original.country.join(', ').toLowerCase();
        const countryB = rowB.original.country.join(', ').toLowerCase();
        return countryA.localeCompare(countryB);
      },
      cell: ({ row }) => {
        const titleContent = formatCountryTooltip(row.original.country);
        return (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-default">
                  {handleRenderCountriesList(row.original.country)}
                </span>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                sideOffset={5}
                className="bg-[#5B5B5B] text-white border-none shadow-lg max-w-[400px] max-h-[300px] overflow-y-auto px-3 py-2 whitespace-normal break-words"
              >
                {titleContent}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
      size: 300
    },
    {
      accessorKey: 'isEnabled',
      header: () => (
        <div className="flex items-center justify-end gap-2 w-full">
          <span>Status</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex cursor-default text-base-mutedForeground">
                  <FontAwesomeIcon icon="fa-regular fa-circle-info" />
                </span>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="bg-[#5B5B5B] text-white border-none shadow-lg max-w-[280px] px-3 py-2"
              >
                Toggling on the switch means the language is now enabled or active.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ),
      cell: ({ row }) => {
        const languageStatus = row.original.language === selectedLanguage?.language
          ? selectedLanguage?.isEnabled
          : row.original.isEnabled;

        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="ml-auto w-fit mr-[21px]">
                  <Switch
                    defaultChecked={languageStatus}
                    onCheckedChange={() => onToggle(row.original)}
                    size="lg"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="bg-[#5B5B5B] text-white border-none shadow-lg max-w-[280px] px-3 py-2 whitespace-normal break-words"
              >
                Activate to make this language available for users, or deactivate to hide it
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
      size: 100
    },
    {
      accessorKey: 'actions',
      header: undefined,
      maxSize: 60,
      minSize: 60,
      size: 60,
      cell: ({ row }) => (
        <div className="max-w-[60px] w-[60px]">
          <ActionCell
            rowData={row.original}
            actions={[
              {
                label: 'Edit',
                handler: () => onEdit(row.original)
              },
              {
                label: 'Delete',
                handler: () => onDelete(row.original)
              }
            ]}
          />
        </div>
      )
    }
  ], [selectedLanguage, onToggle, onEdit, onDelete]);

  return { columns };
};
